import {
  DispatchSource,
  Driver,
  OrderingAlgorithmType,
  Shipment,
  Stop,
  Task,
  TaskStatus,
  useGetDriverTasksByDriverIdLazyQuery,
  useReplaceRouteMutation,
} from "@api/graphql/generated/generated-types";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  DriverAvatar,
  Loading,
} from "@src/common/components";
import { useEffect, useRef, useState } from "react";
import { SIZE } from "baseui/modal";
import ChangeDriverRouteView from "@src/common/components/ChangeDriverRouteView/ChangeDriverRouteView";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import { getUniqueStopDates } from "@src/common/components/Widgets/DispatchCandidatesWidget/utils/assignDriverModalUtils";
import CourieButton from "@src/common/components/Button";
import StopDateListTimeline from "@src/common/components/ChangeDriverRouteView/components/StopDateListTimeline";
import { useAuth } from "@src/common/hooks/useAuth";
import { TransferStopsModal } from "./TransferStopsModal";
import BulkChangeDriverChangeRoutingSlide from "@src/common/components/BulkChangeDriversModal/BulkChangeDriverChangeRoutingSlide";
import {
  getActiveRouteStops,
  mergeRouteStopIds,
  mergeRouteStops,
  useStableRoutingShipment,
} from "./changeRoutingStability";

type Props = {
  isModalOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Orders have moved to another driver. Refresh the page data; the modal
  // stays open so the dispatcher can review the target driver's route.
  onTransferComplete?: () => void;
  shipment: Shipment;
};

type TransferredRoute = {
  driver: Driver;
  stopDates: string[];
};

function ChangeRoutingModal({
  isModalOpen,
  onClose,
  onSuccess,
  onTransferComplete,
  shipment,
}: Props) {
  // Fleet polling re-delivers the shipment with new references (re-signed
  // photo URLs); only routing-relevant changes may reload or reset the route.
  const shipmentSelectedForAction = useStableRoutingShipment(shipment);
  const { courierId, courieUser, employeeId } = useAuth();
  const { showToast } = useCourieStore();
  const [routeDetails, setRouteDetails] =
    useState<Stop[] | undefined>(undefined);
  const [isValidOrder, setIsValidOrder] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [listStopDates, setListStopDates] = useState<string[]>([]);
  const [selectedStopDate, setSelectedStopDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderingAlgorithmType, setSelectedOrderingAlgorithmType] =
    useState<OrderingAlgorithmType | undefined>(OrderingAlgorithmType.Manual);
  const [autoRoutedStopIds, setAutoRoutedStopIds] =
    useState<undefined | string[]>(undefined);
  const [stopIdsToTransfer, setStopIdsToTransfer] =
    useState<string[] | undefined>(undefined);
  // The selection can span stop dates, but routeDetails only holds the day
  // currently shown — remember every stop loaded so far so selected ids from
  // other days can still be resolved to stops when transferring.
  const [stopsSeenById, setStopsSeenById] = useState<Record<string, Stop>>({});
  // Set once a transfer has been applied: the modal then shows the target
  // driver's route instead of the source driver's, the same hand-off the bulk
  // Assign Driver flow makes after quick dispatch.
  const [transferredRoute, setTransferredRoute] =
    useState<TransferredRoute | undefined>(undefined);

  // True until the stop list is seeded from a fresh load (open, date change).
  // Later cache refreshes of the same route are merged into the user's order.
  const shouldReseedRouteRef = useRef(true);

  useEffect(() => {
    if (!isModalOpen) {
      shouldReseedRouteRef.current = true;
      setTransferredRoute(undefined);
      setStopIdsToTransfer(undefined);
      setStopsSeenById({});
      // The next open may be for another driver; a stale route would seed
      // its stop list (and selection counts) with the previous one.
      setRouteDetails(undefined);
    }
  }, [isModalOpen]);

  const isLastStopDate =
    listStopDates === undefined ||
    listStopDates.length === 0 ||
    listStopDates.indexOf(selectedStopDate) === listStopDates.length - 1;

  const [
    getDriverTasksByDriverId,
    {
      data: GetDriverTasksByDriverIdData,
      loading: GetDriverTasksByDriverIdLoading,
      error: GetDriverTasksByDriverIdError,
      refetch: GetDriverTasksByDriverIdRefetch,
    },
  ] = useGetDriverTasksByDriverIdLazyQuery({
    fetchPolicy: "network-only",
  });

  const [
    replaceRouteMutation,
    {
      data: ReplaceRouteMutationData,
      loading: ReplaceRouteMutationLoading,
      error: ReplaceRouteMutationError,
    },
  ] = useReplaceRouteMutation();

  useEffect(() => {
    if (shipmentSelectedForAction) {
      const orderStops = shipmentSelectedForAction.stops || [];
      const filteredStops = orderStops.filter(
        (stop) => stop.completedAt === null
      );
      const listStopDates: undefined | string[] =
        getUniqueStopDates(filteredStops);
      if (listStopDates && listStopDates.length > 1) {
        setListStopDates(listStopDates);
        setSelectedStopDate(listStopDates[0]);
      } else {
        if (listStopDates && listStopDates.length === 1) {
          if (shipmentSelectedForAction?.shipmentDate !== listStopDates[0]) {
            setSelectedStopDate(listStopDates[0]);
            setListStopDates(listStopDates);
            return;
          }
        }
        setListStopDates([]);
        setSelectedStopDate(shipmentSelectedForAction?.shipmentDate);
      }
    }
  }, [shipmentSelectedForAction]);

  useEffect(() => {
    const driverId = shipmentSelectedForAction?.driver?.id;
    if (!driverId || !isModalOpen || !selectedStopDate || !courierId) {
      return;
    }
    const shipmentDate = selectedStopDate;
    shouldReseedRouteRef.current = true;
    getDriverTasksByDriverId({
      variables: {
        courierId: courierId,
        driverId: driverId,
        date: shipmentDate,
        statusFilter: [TaskStatus.Created, TaskStatus.Started],
      },
    }).catch((error) => {
      showErrorToast(error, showToast);
    });
  }, [shipmentSelectedForAction, isModalOpen, selectedStopDate]);

  useEffect(() => {
    if (GetDriverTasksByDriverIdLoading) {
      return;
    }
    if (GetDriverTasksByDriverIdData) {
      if (shouldReseedRouteRef.current) {
        shouldReseedRouteRef.current = false;
        updateRouteDetails();
      } else {
        mergeLiveRouteDetails();
      }
    }
  }, [GetDriverTasksByDriverIdData, GetDriverTasksByDriverIdLoading]);

  const onSubmit = () => {
    if (
      !shipmentSelectedForAction?.driver?.id ||
      !routeDetails ||
      !selectedStopDate ||
      !courierId
    ) {
      return;
    }
    const stopIds = routeDetails.map((stop) => stop.id);
    replaceRouteMutation({
      variables: {
        courierId: courierId,
        driverId: shipmentSelectedForAction.driver.id,
        newStopIds: stopIds,
        shipmentDate: selectedStopDate,
        replacedBy: employeeId ? employeeId : courieUser!.id,
        orderingAlgorithmType: selectedOrderingAlgorithmType,
        autoRoutedStopIds: autoRoutedStopIds ? autoRoutedStopIds : [],
        dispatchSource: DispatchSource.Dispatcher,
      },
    }).then(() => {
      if (isLastStopDate) {
        onSuccess && onSuccess();
      } else {
        const nextStopDateIndex = listStopDates.indexOf(selectedStopDate) + 1;
        setSelectedStopDate(listStopDates[nextStopDateIndex]);
      }
    });
  };

  useEffect(() => {
    if (ReplaceRouteMutationError) {
      showErrorToast(ReplaceRouteMutationError, showToast);
    }
  }, [ReplaceRouteMutationError]);

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handleManualClick = () => {
    setSelectedOrderingAlgorithmType(OrderingAlgorithmType.Manual);
    if (GetDriverTasksByDriverIdData) {
      updateRouteDetails();
    }
  };

  // Cache refresh of the loaded route (ETAs, completed stops, edited stops):
  // refresh stop details and drop/add stops, but keep the unsaved order.
  const mergeLiveRouteDetails = () => {
    const freshStops = getActiveRouteStops(
      GetDriverTasksByDriverIdData?.driverById?.tasks as Task[] | undefined
    ) as Stop[];
    setRouteDetails((current) =>
      current ? mergeRouteStops(current, freshStops) : freshStops
    );
    // Keep the optimizer's order; only follow stops that completed or arrived.
    setAutoRoutedStopIds((current) =>
      mergeRouteStopIds(
        current,
        freshStops.map((stop) => stop.id)
      )
    );
    rememberStops(freshStops);
  };

  const rememberStops = (stops: Stop[]) => {
    setStopsSeenById((current) => {
      const next = { ...current };
      stops.forEach((stop) => {
        next[stop.id] = stop;
      });
      return next;
    });
  };

  const updateRouteDetails = () => {
    const stops = getActiveRouteStops(
      GetDriverTasksByDriverIdData?.driverById?.tasks as Task[] | undefined
    ) as Stop[];
    setRouteDetails(stops);
    const stopIds = stops.map((stop) => stop.id);
    setAutoRoutedStopIds(stopIds);
    rememberStops(stops);
  };

  const stopsToTransfer = (stopIdsToTransfer || [])
    .map((id) => stopsSeenById[id])
    .filter((stop): stop is Stop => !!stop);

  if (transferredRoute) {
    return (
      <BulkChangeDriverChangeRoutingSlide
        isModalOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={() => onSuccess && onSuccess()}
        driver={transferredRoute.driver}
        stopDates={transferredRoute.stopDates}
      />
    );
  }

  return (
    <>
      <BaseModal
        isOpen={isModalOpen}
        closeable={true}
        onClose={handleCloseModal}
        size={SIZE.auto}
        overrides={{
          Dialog: {
            style: {
              overflow: "hidden",
            },
          },
        }}
        autoFocus={false}
      >
        <BaseModalHeader>
          {" "}
          <div className="flex justify-between">
            <div className="flex gap-2 items-center">
              <DriverAvatar
                driverPhoto={shipmentSelectedForAction?.driver?.photoUrl}
                driverName={shipmentSelectedForAction?.driver?.firstName}
                avatarDisplay={
                  shipmentSelectedForAction?.driver?.avatarDisplay || undefined
                }
                size={25}
              />
              <span className="text-gray-900 dark:text-gray-100">
                Confirm Route ({shipmentSelectedForAction?.driver?.firstName})
              </span>
            </div>
            <div className="">
              <StopDateListTimeline
                listStopDates={listStopDates}
                selectedStopDate={selectedStopDate}
                shipmentDate={shipmentSelectedForAction?.shipmentDate || ""}
              />
            </div>
            <span className="mr-3"></span>
          </div>{" "}
        </BaseModalHeader>
        <BaseModalBody
          style={{ minWidth: 1200, height: "70vh", overflow: "scroll" }}
        >
          <ChangeDriverRouteView
            loading={GetDriverTasksByDriverIdLoading}
            routeDetails={routeDetails}
            setRouteDetails={setRouteDetails}
            setIsValidOrder={setIsValidOrder}
            isValidOrder={isValidOrder}
            selectedShipment={shipmentSelectedForAction}
            driver={shipmentSelectedForAction?.driver}
            setErrorMessage={setErrorMessage}
            selectedStopDate={
              listStopDates.length === 0
                ? shipmentSelectedForAction?.shipmentDate
                : selectedStopDate
            }
            onClickManual={() => {
              handleManualClick();
            }}
            setLoading={setIsLoading}
            onAlgorithmTypeChange={setSelectedOrderingAlgorithmType}
            algorithmType={selectedOrderingAlgorithmType}
            setAutoRoutedStopIds={setAutoRoutedStopIds}
            isSelectionEnabled={true}
            onTransferSelection={(selectedStopIds) =>
              setStopIdsToTransfer(selectedStopIds)
            }
          />
        </BaseModalBody>
        <BaseModalFooter className="flex justify-between">
          {listStopDates && listStopDates.length > 1 ? (
            <CourieButton
              color={"secondary"}
              onClick={() => {
                const prevStopDateIndex =
                  listStopDates.indexOf(selectedStopDate) - 1;
                if (prevStopDateIndex >= 0) {
                  setSelectedStopDate(listStopDates[prevStopDateIndex]);
                } else {
                }
              }}
            >
              Back
            </CourieButton>
          ) : (
            <CourieButton color={"secondary"} onClick={handleCloseModal}>
              Cancel
            </CourieButton>
          )}
          <Loading loading={ReplaceRouteMutationLoading} className="flex">
            {errorMessage && (
              <span className="text-red-600 dark:text-red-400 my-auto pr-5">
                {errorMessage}
              </span>
            )}
            <CourieButton
              isProcessing={ReplaceRouteMutationLoading || isLoading}
              onClick={onSubmit}
              disabled={
                !isValidOrder || isLoading || ReplaceRouteMutationLoading
              }
            >
              {isLastStopDate ? `Save` : "Save & Continue"}
            </CourieButton>
          </Loading>
        </BaseModalFooter>
      </BaseModal>
      <TransferStopsModal
        isOpen={!!stopIdsToTransfer && stopIdsToTransfer.length > 0}
        onClose={() => setStopIdsToTransfer(undefined)}
        onTransferred={(driver, stopDates) => {
          setStopIdsToTransfer(undefined);
          onTransferComplete && onTransferComplete();
          setTransferredRoute({ driver, stopDates });
        }}
        onPending={() => {
          setStopIdsToTransfer(undefined);
          onTransferComplete && onTransferComplete();
          handleCloseModal();
        }}
        selectedStops={stopsToTransfer}
        currentDriver={shipmentSelectedForAction?.driver}
        shipmentDate={
          selectedStopDate || shipmentSelectedForAction?.shipmentDate
        }
      />
    </>
  );
}

export default ChangeRoutingModal;
